import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import config from '../config';

const AdvancedBusinessAnalysis = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('12months');
  const [overviewMonthKey, setOverviewMonthKey] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedPeriod]);

  useEffect(() => {
    setOverviewMonthKey(null);
  }, [selectedPeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const digits = selectedPeriod.match(/\d+/);
      const monthsNum = digits ? parseInt(digits[0], 10) : 12;
      const response = await axios.get(`${config.API_URL}/advanced-business/dashboard`, {
        params: { months: monthsNum }
      });
      setDashboardData(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${value >= 0 ? '+' : ''}${value}%`;
  };

  const getGrowthColor = (value) => {
    if (value > 0) return 'text-success';
    if (value < 0) return 'text-danger';
    return 'text-muted';
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'Low': return 'success';
      case 'Medium': return 'warning';
      case 'High': return 'danger';
      default: return 'secondary';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High': return 'danger';
      case 'Medium': return 'warning';
      case 'Low': return 'info';
      default: return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading Advanced Business Analysis...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error!</h4>
          <p>{error}</p>
          <button className="btn btn-outline-danger" onClick={fetchDashboardData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="container-fluid">
        <div className="alert alert-warning" role="alert">
          No data available
        </div>
      </div>
    );
  }

  const {
    executiveSummary,
    financialPerformance,
    salesAnalytics,
    customerInsights,
    productPerformance,
    operationalMetrics,
    growthAnalysis,
    riskAssessment,
    marketInsights,
    strategicRecommendations,
    monthlyRecoveryDrilldown = [],
    analysisMonths = 12
  } = dashboardData;

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">🚀 Advanced Business Analysis</h2>
              <p className="text-muted mb-0">Comprehensive insights and strategic recommendations</p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <select 
                className="form-select form-select-sm" 
                style={{ minWidth: 160 }}
                value={selectedPeriod} 
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="3months">Last 3 Months</option>
                <option value="6months">Last 6 Months</option>
                <option value="12months">Last 12 Months</option>
                <option value="24months">Last 24 Months</option>
              </select>
              <button className="btn btn-outline-primary btn-sm" onClick={fetchDashboardData}>
                <i className="bi bi-arrow-clockwise"></i> Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Executive Summary Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h4 className="card-title">{formatCurrency(executiveSummary.totalRevenue)}</h4>
                  <p className="card-text">Total Revenue</p>
                </div>
                <div className="text-end">
                  <i className="bi bi-graph-up-arrow" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
              <small>
                <span className={getGrowthColor(executiveSummary.revenueGrowth)}>
                  {formatPercentage(executiveSummary.revenueGrowth)}
                </span> vs last month
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h4 className="card-title">{formatCurrency(executiveSummary.totalPaid)}</h4>
                  <p className="card-text">Total Collected</p>
                </div>
                <div className="text-end">
                  <i className="bi bi-check-circle" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
              <small>
                {executiveSummary.recoveryRate}% Recovery Rate
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card bg-warning text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h4 className="card-title">{formatCurrency(executiveSummary.outstandingAmount)}</h4>
                  <p className="card-text">Outstanding</p>
                </div>
                <div className="text-end">
                  <i className="bi bi-clock" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
              <small>
                {executiveSummary.activeEMIs} Active EMIs
              </small>
            </div>
          </div>
        </div>

        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <div>
                  <h4 className="card-title">{executiveSummary.totalCustomers}</h4>
                  <p className="card-text">Total Customers</p>
                </div>
                <div className="text-end">
                  <i className="bi bi-people" style={{ fontSize: '2rem' }}></i>
                </div>
              </div>
              <small>
                <span className={getGrowthColor(executiveSummary.customerGrowth)}>
                  {formatPercentage(executiveSummary.customerGrowth)}
                </span> growth
              </small>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="row mb-4">
        <div className="col-12">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'overview' ? 'active' : ''}`}
                onClick={() => setSelectedTab('overview')}
              >
                📊 Overview
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'financial' ? 'active' : ''}`}
                onClick={() => setSelectedTab('financial')}
              >
                💰 Financial Performance
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'sales' ? 'active' : ''}`}
                onClick={() => setSelectedTab('sales')}
              >
                📈 Sales Analytics
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'customers' ? 'active' : ''}`}
                onClick={() => setSelectedTab('customers')}
              >
                👥 Customer Insights
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'products' ? 'active' : ''}`}
                onClick={() => setSelectedTab('products')}
              >
                📱 Product Performance
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'risk' ? 'active' : ''}`}
                onClick={() => setSelectedTab('risk')}
              >
                ⚠️ Risk Assessment
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'npa' ? 'active' : ''}`}
                onClick={() => setSelectedTab('npa')}
              >
                🚨 NPA Analysis
              </button>
            </li>
            <li className="nav-item">
              <button 
                className={`nav-link ${selectedTab === 'recommendations' ? 'active' : ''}`}
                onClick={() => setSelectedTab('recommendations')}
              >
                💡 Recommendations
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {selectedTab === 'overview' && (
          <div className="row">
            <div className="col-12 mb-4">
              <div className="card border-primary shadow-sm">
                <div className="card-header bg-white py-3 d-flex flex-wrap justify-content-between gap-2 align-items-center">
                  <div>
                    <h5 className="mb-0">📅 Month-wise recovery (EMI due dates)</h5>
                    <small className="text-muted">
                      Har row = us calendar month jisme next unpaid EMI installment due hai. Row pe click karen to
                      niche us mahine EMI-due customers ki list aur is month total EMI collection dikhega.
                    </small>
                  </div>
                  <span className="badge bg-secondary align-self-start">window: {analysisMonths} mo.</span>
                </div>
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Month</th>
                        <th className="text-end">Is month EMI collection</th>
                        <th className="text-end">EMIs (installment due)</th>
                        <th className="text-end">Est. EMI installment</th>
                        <th className="text-end">Outstanding (those EMIs)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthlyRecoveryDrilldown.map((row) => {
                        const isOpen = overviewMonthKey === row.month;
                        return (
                          <tr
                            key={row.yearMonthKey || row.month}
                            role="button"
                            className={isOpen ? 'table-active' : ''}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setOverviewMonthKey(isOpen ? null : row.month)}
                          >
                            <td><strong>{row.month}</strong></td>
                            <td className="text-end text-success">{formatCurrency(row.collectionsInMonth)}</td>
                            <td className="text-end">{row.emisDue}</td>
                            <td className="text-end">{formatCurrency(row.installmentDemandApprox)}</td>
                            <td className="text-end text-danger fw-semibold">{formatCurrency(row.recoveryPendingApprox)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {overviewMonthKey && (
                  <div className="card-body border-top bg-light">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <strong>{overviewMonthKey} — EMI due customers</strong>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setOverviewMonthKey(null)}
                      >
                        Close
                      </button>
                    </div>
                    {(() => {
                      const bucket = monthlyRecoveryDrilldown.find((r) => r.month === overviewMonthKey);
                      if (!bucket || !bucket.customers?.length) {
                        return (
                          <p className="text-muted mb-0 small">
                            Is month EMI-due aur abhi balance rehne wala koi account match nahi hua — phir bhi upar
                            &quot;collection&quot; us month ke saare EMI payments ka jod hai.
                          </p>
                        );
                      }
                      return (
                        <div className="table-responsive">
                          <table className="table table-sm table-bordered bg-white mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>Customer</th>
                                <th>Phone</th>
                                <th>Due</th>
                                <th>Inst.</th>
                                <th className="text-end">Outstanding</th>
                                <th className="text-end">Approx EMI</th>
                                <th />
                              </tr>
                            </thead>
                            <tbody>
                              {bucket.customers.map((c) => (
                                <tr key={String(c.emiId)}>
                                  <td>{c.customerName}</td>
                                  <td className="text-nowrap">{c.phone}</td>
                                  <td className="text-nowrap small">{c.installmentDueFormatted}</td>
                                  <td>{c.installmentNumber}</td>
                                  <td className="text-end">{formatCurrency(c.outstandingOnPlan)}</td>
                                  <td className="text-end">{formatCurrency(c.installmentDueApprox)}</td>
                                  <td className="text-end">
                                    <Link
                                      className="btn btn-sm btn-primary"
                                      to={`/emi/${encodeURIComponent(String(c.emiId))}`}
                                    >
                                      Payment / EMI
                                    </Link>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>📈 Growth Analysis</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-6">
                      <h4 className={getGrowthColor(growthAnalysis.yearOverYearGrowth)}>
                        {formatPercentage(growthAnalysis.yearOverYearGrowth)}
                      </h4>
                      <small className="text-muted">Year-over-Year Growth</small>
                    </div>
                    <div className="col-6">
                      <h4 className={getGrowthColor(growthAnalysis.averageRecentGrowth)}>
                        {formatPercentage(growthAnalysis.averageRecentGrowth)}
                      </h4>
                      <small className="text-muted">Recent Growth Trend</small>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="progress">
                      <div 
                        className={`progress-bar ${growthAnalysis.growthTrend === 'positive' ? 'bg-success' : growthAnalysis.growthTrend === 'negative' ? 'bg-danger' : 'bg-warning'}`}
                        style={{ width: `${Math.abs(growthAnalysis.averageRecentGrowth)}%` }}
                      ></div>
                    </div>
                    <small className="text-muted">Growth Trend: {growthAnalysis.growthTrend}</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>⚠️ Risk Assessment</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-6">
                      <h4 className={`text-${getRiskColor(riskAssessment.riskLevel)}`}>
                        {riskAssessment.riskLevel}
                      </h4>
                      <small className="text-muted">Risk Level</small>
                    </div>
                    <div className="col-6">
                      <h4>{riskAssessment.portfolioRiskScore}%</h4>
                      <small className="text-muted">Portfolio Risk Score</small>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="progress">
                      <div 
                        className={`progress-bar bg-${getRiskColor(riskAssessment.riskLevel)}`}
                        style={{ width: `${riskAssessment.portfolioRiskScore}%` }}
                      ></div>
                    </div>
                    <small className="text-muted">Outstanding: {formatCurrency(riskAssessment.totalOutstanding)}</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5>💡 Key Strategic Recommendations</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {strategicRecommendations.slice(0, 3).map((rec, index) => (
                      <div key={index} className="col-md-4 mb-3">
                        <div className="card border-start border-4 border-primary">
                          <div className="card-body">
                            <div className="d-flex justify-content-between align-items-start">
                              <h6 className="card-title">{rec.title}</h6>
                              <span className={`badge bg-${getPriorityColor(rec.priority)}`}>
                                {rec.priority}
                              </span>
                            </div>
                            <p className="card-text small">{rec.description}</p>
                            <small className="text-muted">{rec.action}</small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Financial Performance Tab */}
        {selectedTab === 'financial' && (
          <div className="row">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>📊 Monthly Financial Performance</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Revenue</th>
                          <th>Collections</th>
                          <th>Expenses</th>
                          <th>Late fees</th>
                          <th>NPA adj.</th>
                          <th>Return adj.</th>
                          <th>Profit</th>
                          <th>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {financialPerformance.monthlyPerformance.map((month, index) => (
                          <tr key={index}>
                            <td><strong>{month.month}</strong></td>
                            <td>{formatCurrency(month.revenue)}</td>
                            <td>{formatCurrency(month.paid)}</td>
                            <td>{formatCurrency(month.expenses)}</td>
                            <td className="text-success">{formatCurrency(month.lateFeeCollection || 0)}</td>
                            <td className={(month.npaRecoveryExtra || 0) - (month.npaLoss || 0) >= 0 ? 'text-success' : 'text-danger'}>
                              {formatCurrency((month.npaRecoveryExtra || 0) - (month.npaLoss || 0))}
                            </td>
                            <td className={(month.returnAdj || 0) >= 0 ? 'text-success' : 'text-danger'}>
                              {formatCurrency(month.returnAdj || 0)}
                            </td>
                            <td className={month.profit >= 0 ? 'text-success' : 'text-danger'}>
                              {formatCurrency(month.profit)}
                            </td>
                            <td>
                              <span className={`badge ${month.profitMargin >= 20 ? 'bg-success' : month.profitMargin >= 10 ? 'bg-warning' : 'bg-danger'}`}>
                                {month.profitMargin}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>💰 Financial Summary</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <h6>Total Revenue</h6>
                    <h4 className="text-primary">{formatCurrency(financialPerformance.totalRevenue)}</h4>
                  </div>
                  <div className="mb-3">
                    <h6>Total Profit</h6>
                    <h4 className="text-success">{formatCurrency(financialPerformance.totalProfit)}</h4>
                    <small className="text-muted">
                      Includes late fees ₹{formatCurrency(financialPerformance.lateFeeCollection || 0)}
                      {financialPerformance.npaRecoveryExtra > 0 && ` · NPA extra ₹${formatCurrency(financialPerformance.npaRecoveryExtra)}`}
                      {financialPerformance.npaLoss > 0 && ` · NPA loss −₹${formatCurrency(financialPerformance.npaLoss)}`}
                      {financialPerformance.returnAdj != null && financialPerformance.returnAdj !== 0 &&
                        ` · Return adj. ₹${formatCurrency(financialPerformance.returnAdj)}`}
                    </small>
                  </div>
                  <div className="mb-3">
                    <h6>Profit Margin</h6>
                    <h4 className="text-info">{financialPerformance.profitMargin}%</h4>
                  </div>
                  <div className="mb-3">
                    <h6>Cash Flow</h6>
                    <h4 className={financialPerformance.cashFlow >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(financialPerformance.cashFlow)}
                    </h4>
                  </div>
                  <div className="mb-3">
                    <h6>Working Capital</h6>
                    <h4 className="text-warning">{formatCurrency(financialPerformance.workingCapital)}</h4>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Analytics Tab */}
        {selectedTab === 'sales' && (
          <div className="row">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>📈 Sales Trend Analysis</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Month</th>
                          <th>Revenue</th>
                          <th>Sales Count</th>
                          <th>Average Sale</th>
                          <th>Growth</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesAnalytics.monthlyTrend.map((month, index) => (
                          <tr key={index}>
                            <td><strong>{month.month}</strong></td>
                            <td>{formatCurrency(month.revenue)}</td>
                            <td>
                              <span className="badge bg-primary">{month.count}</span>
                            </td>
                            <td>{formatCurrency(month.average)}</td>
                            <td>
                              <span className={`badge ${month.growth >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                {formatPercentage(month.growth || 0)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>🏆 Top Products</h5>
                </div>
                <div className="card-body">
                  {salesAnalytics.topProducts.slice(0, 5).map((product, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                      <div>
                        <strong>{product.name}</strong>
                        <br />
                        <small className="text-muted">{product.count} sales</small>
                      </div>
                      <div className="text-end">
                        <strong>{formatCurrency(product.revenue)}</strong>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5>📊 Category Performance</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {Object.entries(salesAnalytics.categoryPerformance).map(([category, data]) => (
                      <div key={category} className="col-md-3 mb-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h6 className="card-title">{category}</h6>
                            <h4 className="text-primary">{formatCurrency(data.revenue)}</h4>
                            <p className="card-text">
                              <span className="badge bg-info">{data.count} sales</span>
                            </p>
                            <small className="text-muted">
                              Avg: {formatCurrency(data.average)}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Customer Insights Tab */}
        {selectedTab === 'customers' && (
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>👥 Customer Segmentation</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-4">
                      <h4 className="text-success">{customerInsights.customerSegments.highValue}</h4>
                      <small className="text-muted">High Value</small>
                    </div>
                    <div className="col-4">
                      <h4 className="text-warning">{customerInsights.customerSegments.mediumValue}</h4>
                      <small className="text-muted">Medium Value</small>
                    </div>
                    <div className="col-4">
                      <h4 className="text-info">{customerInsights.customerSegments.lowValue}</h4>
                      <small className="text-muted">Low Value</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>💳 Payment Behavior</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-4">
                      <h4 className="text-success">{customerInsights.paymentBehavior.onTime}</h4>
                      <small className="text-muted">On Time</small>
                    </div>
                    <div className="col-4">
                      <h4 className="text-warning">{customerInsights.paymentBehavior.delayed}</h4>
                      <small className="text-muted">Delayed</small>
                    </div>
                    <div className="col-4">
                      <h4 className="text-danger">{customerInsights.paymentBehavior.defaulted}</h4>
                      <small className="text-muted">Defaulted</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5>📊 Customer Analytics</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-3">
                      <div className="text-center">
                        <h4 className="text-primary">{customerInsights.totalCustomers}</h4>
                        <small className="text-muted">Total Customers</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h4 className="text-success">{customerInsights.activeCustomers}</h4>
                        <small className="text-muted">Active Customers</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h4 className="text-warning">{customerInsights.loyalCustomers}</h4>
                        <small className="text-muted">Loyal Customers</small>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="text-center">
                        <h4 className="text-info">{customerInsights.loyaltyRate}%</h4>
                        <small className="text-muted">Loyalty Rate</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Product Performance Tab */}
        {selectedTab === 'products' && (
          <div className="row">
            <div className="col-md-8">
              <div className="card">
                <div className="card-header">
                  <h5>📱 Product Performance</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th>Revenue</th>
                          <th>Sales</th>
                          <th>Profit</th>
                          <th>Margin</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productPerformance.topProducts.map((product, index) => (
                          <tr key={index}>
                            <td><strong>{product.name}</strong></td>
                            <td>
                              <span className="badge bg-secondary">{product.category}</span>
                            </td>
                            <td>{formatCurrency(product.revenue)}</td>
                            <td>
                              <span className="badge bg-primary">{product.count}</span>
                            </td>
                            <td className={product.profit >= 0 ? 'text-success' : 'text-danger'}>
                              {formatCurrency(product.profit)}
                            </td>
                            <td>
                              <span className={`badge ${product.profitMargin >= 20 ? 'bg-success' : product.profitMargin >= 10 ? 'bg-warning' : 'bg-danger'}`}>
                                {product.profitMargin}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card">
                <div className="card-header">
                  <h5>📊 Product Summary</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <h6>Total Products</h6>
                    <h4 className="text-primary">{productPerformance.totalProducts}</h4>
                  </div>
                  <div className="mb-3">
                    <h6>Products with Sales</h6>
                    <h4 className="text-success">{productPerformance.productsWithSales}</h4>
                  </div>
                  <div className="mb-3">
                    <h6>Average Revenue</h6>
                    <h4 className="text-info">{formatCurrency(productPerformance.averageProductRevenue)}</h4>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5>📦 Category Performance</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {Object.entries(productPerformance.categoryPerformance).map(([category, data]) => (
                      <div key={category} className="col-md-4 mb-3">
                        <div className="card text-center">
                          <div className="card-body">
                            <h6 className="card-title">{category}</h6>
                            <h4 className="text-primary">{formatCurrency(data.revenue)}</h4>
                            <p className="card-text">
                              <span className="badge bg-info">{data.count} sales</span>
                              <span className="badge bg-success ms-1">{data.products} products</span>
                            </p>
                            <small className="text-muted">
                              Margin: {data.profitMargin}%
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Risk Assessment Tab */}
        {selectedTab === 'risk' && (
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>⚠️ Portfolio Risk</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-4">
                      <h4 className="text-success">{riskAssessment.defaultRisk.low}</h4>
                      <small className="text-muted">Low Risk</small>
                    </div>
                    <div className="col-4">
                      <h4 className="text-warning">{riskAssessment.defaultRisk.medium}</h4>
                      <small className="text-muted">Medium Risk</small>
                    </div>
                    <div className="col-4">
                      <h4 className="text-danger">{riskAssessment.defaultRisk.high}</h4>
                      <small className="text-muted">High Risk</small>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="progress">
                      <div 
                        className={`progress-bar bg-${getRiskColor(riskAssessment.riskLevel)}`}
                        style={{ width: `${riskAssessment.portfolioRiskScore}%` }}
                      ></div>
                    </div>
                    <small className="text-muted">Portfolio Risk Score: {riskAssessment.portfolioRiskScore}%</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5>💡 Risk Recommendations</h5>
                </div>
                <div className="card-body">
                  <ul className="list-group list-group-flush">
                    {riskAssessment.recommendations.map((rec, index) => (
                      <li key={index} className="list-group-item">
                        <small>{rec}</small>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header">
                  <h5>🚨 High Risk Customers</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Total Amount</th>
                          <th>Paid</th>
                          <th>Outstanding</th>
                          <th>EMI Count</th>
                          <th>Risk Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskAssessment.highRiskCustomers.map((customer, index) => (
                          <tr key={index}>
                            <td><strong>{customer.customerName}</strong></td>
                            <td>{formatCurrency(customer.totalAmount)}</td>
                            <td>{formatCurrency(customer.totalPaid)}</td>
                            <td className="text-danger">{formatCurrency(customer.outstanding)}</td>
                            <td>
                              <span className="badge bg-primary">{customer.emiCount}</span>
                            </td>
                            <td>
                              <span className={`badge ${customer.riskScore > 70 ? 'bg-danger' : customer.riskScore > 40 ? 'bg-warning' : 'bg-success'}`}>
                                {customer.riskScore}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* NPA Analysis Tab */}
        {selectedTab === 'npa' && (
          <div className="row">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-danger text-white">
                  <h5>🚨 NPA Classification (Bank-style)</h5>
                </div>
                <div className="card-body">
                  <div className="row text-center">
                    <div className="col-3">
                      <h4 className="text-success">{riskAssessment.npaAnalysis.standard}</h4>
                      <small className="text-muted">Standard<br/>(0-30 days)</small>
                    </div>
                    <div className="col-3">
                      <h4 className="text-warning">{riskAssessment.npaAnalysis.substandard}</h4>
                      <small className="text-muted">Substandard<br/>(31-50 days)</small>
                    </div>
                    <div className="col-3">
                      <h4 className="text-danger">{riskAssessment.npaAnalysis.doubtful}</h4>
                      <small className="text-muted">Doubtful<br/>(51-90 days)</small>
                    </div>
                    <div className="col-3">
                      <h4 className="text-dark">{riskAssessment.npaAnalysis.loss}</h4>
                      <small className="text-muted">Loss<br/>(90+ days)</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-danger text-white">
                  <h5>💰 NPA Financial Impact</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <h6>Total NPA Amount</h6>
                    <h4 className="text-danger">{formatCurrency(riskAssessment.totalNPAAmount)}</h4>
                  </div>
                  <div className="mb-3">
                    <h6>NPA Percentage</h6>
                    <h4 className={riskAssessment.npaPercentage > 15 ? 'text-danger' : riskAssessment.npaPercentage > 8 ? 'text-warning' : 'text-success'}>
                      {riskAssessment.npaPercentage}%
                    </h4>
                  </div>
                  <div className="mb-3">
                    <h6>Critical NPA Amount</h6>
                    <h4 className="text-danger">{formatCurrency(riskAssessment.criticalNPAAmount)}</h4>
                    <small className="text-muted">Doubtful + Loss (50+ days)</small>
                  </div>
                  <div className="mb-3">
                    <h6>Total Critical Defaulters</h6>
                    <h4 className="text-danger">{riskAssessment.totalCriticalDefaulters}</h4>
                    <small className="text-muted">Paisa dubne ka pure chance</small>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header bg-danger text-white">
                  <h5>🚨 Critical Defaulters (50+ Days Overdue)</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Customer</th>
                          <th>Outstanding Amount</th>
                          <th>Overdue Days</th>
                          <th>Risk Level</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskAssessment.criticalDefaulters.map((customer, index) => (
                          <tr key={index}>
                            <td><strong>{customer.customerName}</strong></td>
                            <td className="text-danger">{formatCurrency(customer.outstandingAmount)}</td>
                            <td>
                              <span className={`badge ${customer.overdueDays > 90 ? 'bg-dark' : 'bg-danger'}`}>
                                {customer.overdueDays} days
                              </span>
                            </td>
                            <td>
                              <span className={`badge ${customer.riskLevel === 'Loss' ? 'bg-dark' : 'bg-danger'}`}>
                                {customer.riskLevel}
                              </span>
                            </td>
                            <td>
                              {customer.overdueDays > 90 ? (
                                <span className="badge bg-dark">💀 Total Loss</span>
                              ) : (
                                <span className="badge bg-danger">❓ Doubtful</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 mt-4">
              <div className="card">
                <div className="card-header bg-warning text-dark">
                  <h5>💡 NPA Recommendations</h5>
                </div>
                <div className="card-body">
                  <div className="row">
                    {riskAssessment.npaRecommendations.map((rec, index) => (
                      <div key={index} className="col-md-6 mb-3">
                        <div className="alert alert-warning">
                          <small>{rec}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Strategic Recommendations Tab */}
        {selectedTab === 'recommendations' && (
          <div className="row">
            {strategicRecommendations.map((rec, index) => (
              <div key={index} className="col-md-6 mb-4">
                <div className="card h-100">
                  <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{rec.title}</h5>
                    <span className={`badge bg-${getPriorityColor(rec.priority)}`}>
                      {rec.priority} Priority
                    </span>
                  </div>
                  <div className="card-body">
                    <h6 className="card-subtitle mb-2 text-muted">{rec.category}</h6>
                    <p className="card-text">{rec.description}</p>
                    <div className="mt-3">
                      <h6>Recommended Action:</h6>
                      <p className="text-primary">{rec.action}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="row mt-4">
        <div className="col-12">
          <div className="card">
            <div className="card-body text-center text-muted">
              <small>
                Last updated: {new Date(dashboardData.lastUpdated).toLocaleString()} | 
                Advanced Business Analysis Dashboard
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedBusinessAnalysis;
